import React from 'react';
import styled from 'styled-components';
import { MapPin, Phone, Mail, Clock, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ContactContainer = styled.section`
  padding: 60px 0;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h2 {
    font-size: 2.5rem;
    font-weight: 300;
    color: #2c2c2c;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c2c2c;
    margin-bottom: 30px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f4f0;
  border-radius: 15px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(5px);
  }

  .icon {
    width: 50px;
    height: 50px;
    background: white;
    border: 2px solid #333;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
    flex-shrink: 0;
  }

  .content {
    h4 {
      font-size: 1rem;
      font-weight: 600;
      color: #2c2c2c;
      margin-bottom: 5px;
    }

    p {
      font-size: 0.9rem;
      color: #666;
      margin: 0;
    }
  }
`;

const MapContainer = styled.div`
  height: 600px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const MapIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: #333;
  color: #fff;
  border: none;
  padding: 15px 30px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: #6d3410;
    transform: translateY(-2px);
  }

  &.secondary {
    background: transparent;
    color: #333;
    border: 2px solid #333;

    &:hover {
      background: #333;
      color: #fff;
    }
  }
`;

const ContactSection: React.FC = () => {
  const navigate = useNavigate();

  const handleAppointmentClick = () => {
    navigate('/randevu');
  };

  const handleCallClick = () => {
    window.open('tel:02242215120', '_self');
  };

  return (
    <ContactContainer>
      <Container>
        <SectionHeader>
          <h2>İletişim</h2>
          <p>
            Hayalinizdeki gelinliği bulmak için bizimle iletişime geçin. 
            Randevu alın ve showroomumuzu ziyaret edin.
          </p>
        </SectionHeader>

        <ContactGrid>
          <ContactInfo>
            <h3>İletişim Bilgileri</h3>
            
            <ContactItem>
              <div className="icon">
                <MapPin size={20} />
              </div>
              <div className="content">
                <h4>Adres</h4>
                <p>Nalbantoğlu, 2. Bademli Sokak, Alyanak Apartmanı No: 5/B<br />Osmangazi/Bursa, Türkiye</p>
              </div>
            </ContactItem>

            <ContactItem>
              <div className="icon">
                <Phone size={20} />
              </div>
              <div className="content">
                <h4>Telefon</h4>
                <p>0224 221 51 20</p>
              </div>
            </ContactItem>

            <ContactItem>
              <div className="icon">
                <Mail size={20} />
              </div>
              <div className="content">
                <h4>E-posta</h4>
                <p>info@saraylimoda.com<br />randevu@saraylimoda.com</p>
              </div>
            </ContactItem>

            <ContactItem>
              <div className="icon">
                <Clock size={20} />
              </div>
              <div className="content">
                <h4>Çalışma Saatleri</h4>
                <p>Pazartesi - Cumartesi<br />10:00 - 19:00</p>
              </div>
            </ContactItem>
          </ContactInfo>

          <MapContainer>
            <MapIframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3048.1761305378727!2d29.06036461240583!3d40.18289357135909!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14ca3d8160289eb3%3A0xeaa6dd58e25128a2!2sSarayl%C4%B1%20Moda%20Bursa%20Gelinlik!5e0!3m2!1str!2str!4v1761475022026!5m2!1str!2str"
              width="100%"
              height="600"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Saraylı Moda Dükkan Konumu"
            />
          </MapContainer>
        </ContactGrid>

        <ActionButtons>
          <ActionButton onClick={handleAppointmentClick}>
            <Calendar size={20} />
            Randevu Al
          </ActionButton>
          <ActionButton className="secondary" onClick={handleCallClick}>
            <Phone size={20} />
            Hemen Ara
          </ActionButton>
        </ActionButtons>
      </Container>
    </ContactContainer>
  );
};

export default ContactSection;
